import React from 'react';
import styled from 'styled-components';
import {
  screenSmMax,
  spacer4,
  spacer8,
  spacer20,
  spacer40,
  ButtonIcon,
  IconChevronLeft,
  IconChevronRight,
  cssColorValue,
  spacer24,
} from '@spotify-internal/encore-web-v5';
import {
  DELAYS,
  fadeInAnimation,
  Grid,
  withAnimationContext,
} from '@spotify-internal/frodor-modules';
import AliceCarousel from 'react-alice-carousel';
import 'react-alice-carousel/lib/alice-carousel.css';
import { ModuleType } from '../types';
import { ContentfulAsset } from '../../Contentful';
import { ContentfulImage } from '../../Contentful/ContentfulImage';
import {
  ImageAspectBox1x1,
  HeadlineWithSupertextComponent,
  FeaturedContentHeadline,
} from '../../ui';
import { formatText } from '../../utils';
import { useTracking } from '../../Tracking';
import { useT } from '@spotify-internal/i18n-core';

const MiniCarouselNoHero = styled(Grid)`
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.box.background};
  color: ${({ theme }) => theme.box.text};
  padding-top: ${spacer24};
  padding-bottom: ${spacer24};
`;

// TODO: find a better way to reorder than using separated styled components
const HeadlineWithSupertextDesktop = styled.div`
  @media (max-width: ${screenSmMax}) {
    display: none;
  }
`;

const HeadlineWithSupertextMobile = styled.div`
  display: none;
  @media (max-width: ${screenSmMax}) {
    display: initial;
  }
`;

const Bottom = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  padding-top: ${spacer40};
  padding-bottom: ${spacer20};

  @media (max-width: ${screenSmMax}) {
    padding: ${spacer20} 0px;
    flex-direction: column;
  }
`;

const Left = styled.div.attrs({
  variant: 'ballad',
})`
  width: 45%;
  margin-right: 180px;

  @media (max-width: ${screenSmMax}) {
    padding-right: 0px;
    width: 100%;
  }
`;

export const Arrow = styled(ButtonIcon)`
  padding: 0 ${spacer4};

  svg {
    opacity: 0;
  }

  &:focus:not(:disabled) {
    svg {
      opacity: 1;
      border-bottom: 0;
    }
  }

  @media (max-width: ${screenSmMax}) {
    padding: 0 0;
    svg {
      opacity: 1;
    }
  }
`;

const CarouselContainer = styled.div`
  direction: ltr;

  width: 35%;
  margin-right: 75px;
  align-items: center;
  display: flex;

  @media (max-width: ${screenSmMax}) {
    padding-left: 0px;
    height: auto;
    display: flex;
    justify-content: center;
    width: 100%;
  }

  @media (max-width: ${screenSmMax}) {
    .alice-carousel {
      width: 80%;
    }
  }

  .alice-carousel__dots {
    margin: 0px;
    padding-top: ${spacer8};
    height: ${spacer20};
    display: flex;
    justify-content: center;
    width: 100%;
  }

  .alice-carousel__dots-item {
    width: ${spacer8};
    height: ${spacer8};
    border-radius: 50%;
    border: none;
    background-color: ${cssColorValue('backgroundPress')};
    margin-left: ${spacer4};
    margin-right: ${spacer4};
    padding: 0px;
  }

  .alice-carousel__dots-item.__active {
    background-color: ${({ theme }) => theme.box.text};
  }
  @media (min-width: ${screenSmMax}) {
    &:hover {
      ${Arrow} svg {
        opacity: 1;
        color: ${({ theme }) => theme.box.text};
      }
    }
  }
`;

const Body = withAnimationContext(
  styled.div`
    ${fadeInAnimation(DELAYS.THIRD)};
  `,
);

const ImageContainer = styled(ImageAspectBox1x1)``;

export type MiniCarouselNoHeroModuleType = ModuleType & {
  title?: string;
  supertext?: string;
  headline?: string;
  body?: string;
  image?: ContentfulAsset;
  caption?: string;
  carouselSlides?: any[];
  contentType: string;
};

export const MiniCarouselNoHeroModule = React.forwardRef<
  React.MutableRefObject<ModuleType>,
  MiniCarouselNoHeroModuleType
>(
  (
    {
      title = '',
      headline = '',
      supertext = '',
      body = '',
      image,
      caption = '',
      carouselSlides = [],
      contentType = '',
    },
    moduleRef,
  ) => {
    const t = useT();
    const slides = carouselSlides
      .filter(Boolean)
      .map(asset => asset.fields || asset);
    const fchProps = {
      supertext,
      headline,
      image,
      useFeaturedContentHeadline: false,
      caption,
    };

    const ref = React.useRef<AliceCarousel>(null);
    const { sendEvent } = useTracking();

    const fireEvent = React.useCallback(
      (action: string, label: string = title) => {
        sendEvent({
          eventCategory: `Frodor - module-${contentType}`,
          eventAction: action,
          eventLabel: label,
        });
      },
      [title],
    );

    const handleClickOnPrevButton = () => {
      const { current } = ref;

      if (!current) {
        return;
      }

      const { slidePrev } = current;

      slidePrev();
      fireEvent('click', 'Arrow');
    };

    const handleClickOnNextButton = () => {
      const { current } = ref;

      if (!current) {
        return;
      }

      const { slideNext } = current;

      slideNext();
      fireEvent('click', 'Arrow');
    };

    return (
      <MiniCarouselNoHero>
        <HeadlineWithSupertextMobile>
          <HeadlineWithSupertextComponent
            supertext={supertext}
            headline={headline}
          />
        </HeadlineWithSupertextMobile>
        {image && <FeaturedContentHeadline {...fchProps} />}
        <Bottom ref={moduleRef as React.RefObject<HTMLDivElement>}>
          <Left>
            <HeadlineWithSupertextDesktop>
              <HeadlineWithSupertextComponent
                supertext={supertext}
                headline={headline}
              />
            </HeadlineWithSupertextDesktop>
            <Body dangerouslySetInnerHTML={{ __html: formatText(body) }} />
          </Left>
          <CarouselContainer>
            <Arrow
              onClick={handleClickOnPrevButton}
              aria-label={t('FRODOR_6b0335', 'Go to previous slide', '')}
            >
              <IconChevronLeft iconSize={32} />
            </Arrow>
            <AliceCarousel
              items={slides.map((carouselImage, index) => (
                <ImageContainer key={index}>
                  {carouselImage && <ContentfulImage asset={carouselImage} />}
                </ImageContainer>
              ))}
              autoPlayInterval={4000}
              autoPlayDirection="ltr"
              autoPlay
              fadeOutAnimation
              mouseTrackingEnabled
              buttonsDisabled
              ref={ref}
              playButtonEnabled={false}
              disableAutoPlayOnAction
              duration={1000}
            />
            <Arrow
              onClick={handleClickOnNextButton}
              aria-label={t('FRODOR_2f86dc', 'Go to next slide', '')}
            >
              <IconChevronRight iconSize={32} />
            </Arrow>
          </CarouselContainer>
        </Bottom>
      </MiniCarouselNoHero>
    );
  },
);
